import { z } from 'zod';

import { SCHEDULE_UNIT } from 'constants/enums';
import {
  convertSendAtToDBValue,
  convertSendAtToFormValue
} from 'pages/dashboard/overview/events/schemas/commonUtils';
import dayjs from 'utils/extendedDayJS';

export const FEEDBACK_ANONYMITY = {
  ANONYMOUS: 'anonymous',
  NOT_ANONYMOUS: 'not-anonymous'
} as const;

export const FEEDBACK_SCHEDULE_TYPE = {
  BEFORE_EVENT_ENDS: 'before-event-ends',
  AFTER_EVENT_ENDS: 'after-event-ends'
} as const;

/**
 * Form Data Structure:
 * {
 *   isFeedbackAnonymous: FEEDBACK_ANONYMITY;
 *   feedbackSchedule: {
 *     quantity?: number;
 *     unit?: SCHEDULE_UNIT;
 *     beforeEventEnds?: boolean;
 *     sendAt?: Date;  // Date in form - local time
 *   };
 *   notificationChannel: {
 *     email: boolean;
 *     slack: boolean;
 *   };
 * }
 *
 * Database Data Structure:
 * Same as above
 *  - feedbackSchedule.sendAt is string in UTC format
 *  - isFeedbackAnonymous is boolean
 */

const FeedbackScheduleSchema = (
  datetimeStart: string,
  datetimeEnd: string,
  eventTimezone?: string,
  scheduledPublishAt?: string
): z.ZodType<any> =>
  z
    .object({
      // TODO: Check why is this any?
      quantity: z.any().optional(),
      unit: z.nativeEnum(SCHEDULE_UNIT).optional(),
      beforeEventEnds: z
        .nativeEnum(FEEDBACK_SCHEDULE_TYPE)
        .transform((value: string): boolean => value === FEEDBACK_SCHEDULE_TYPE.BEFORE_EVENT_ENDS)
        .optional(),
      sendAt: z
        .date()
        .transform((date: Date): string | undefined =>
          date ? convertSendAtToDBValue(date, eventTimezone) : undefined
        )
        .optional()
    })
    .superRefine((data: any, ctx) => {
      const hasQuantity = data.quantity !== undefined;
      const hasUnit = data.unit !== undefined;
      const hasBeforeEventEnds = data.beforeEventEnds !== undefined;

      const allFieldsPresent = hasQuantity && hasUnit && hasBeforeEventEnds;
      const noFieldsPresent = !hasQuantity && !hasUnit && !hasBeforeEventEnds;

      // Validate quantity if present
      // TODO: Why is this needed?
      if (hasQuantity) {
        const quantity = Number(data.quantity);
        if (Number.isNaN(quantity) || !Number.isInteger(quantity) || quantity < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Quantity must be at least 1',
            path: ['beforeEventEnds']
          });
        }
      }

      if (!allFieldsPresent && !noFieldsPresent) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please fill in all schedule fields',
          path: ['beforeEventEnds']
        });
      }

      if (noFieldsPresent && !data.sendAt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Date and time are required',
          path: ['sendAt']
        });
      }

      // sendAt must be in the future
      if (data.sendAt && dayjs.utc(data.sendAt).isBefore(dayjs.utc())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Scheduled time must be in the future',
          path: ['sendAt']
        });
      }

      // sendAt must be after scheduled publish time
      if (
        scheduledPublishAt &&
        data.sendAt &&
        dayjs.utc(data.sendAt).isBefore(dayjs.utc(scheduledPublishAt))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Scheduled time must be after the event is scheduled for publishing',
          path: ['beforeEventEnds'],
          fatal: true
        });
      }

      // sendAt must be after event starts
      if (data.sendAt && dayjs.utc(data.sendAt).isBefore(dayjs.utc(datetimeStart))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Scheduled time can't be before the event starts",
          path: ['sendAt']
        });
      }

      if (allFieldsPresent) {
        const calculatedSendAt = dayjs.subtractOrAddTimeFromDateTime(
          datetimeEnd,
          data.quantity,
          data.unit,
          data.beforeEventEnds,
          'UTC'
        );

        // relative time must be in the future
        if (dayjs.utc(calculatedSendAt).isBefore(dayjs.utc())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Scheduled time must be in the future',
            path: ['beforeEventEnds']
          });
        }

        // relative time must be after scheduled publish time
        if (
          scheduledPublishAt &&
          dayjs.utc(calculatedSendAt).isBefore(dayjs.utc(scheduledPublishAt))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Scheduled time must be after the event is scheduled for publishing',
            path: ['beforeEventEnds'],
            fatal: true
          });
        }

        // relative time must be after event starts
        if (dayjs.utc(calculatedSendAt).isBefore(dayjs.utc(datetimeStart))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Scheduled time can't be before the event starts",
            path: ['beforeEventEnds']
          });
        }
      }
    });

const NotificationChannelSchema = z
  .object({
    email: z.boolean().optional(),
    slack: z.boolean().optional()
  })
  .superRefine((data, ctx) => {
    if (!data.email && !data.slack) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'You must have one notification method selected',
        path: ['slack']
      });
    }
  });

export const getFeedbackScheduleSchema = (
  datetimeStart: string,
  datetimeEnd: string,
  eventTimezone?: string,
  scheduledPublishAt?: string
): z.ZodType<any> =>
  z.object({
    isFeedbackAnonymous: z
      .enum([FEEDBACK_ANONYMITY.ANONYMOUS, FEEDBACK_ANONYMITY.NOT_ANONYMOUS])
      .transform((value: string): boolean => value === FEEDBACK_ANONYMITY.ANONYMOUS),
    feedbackSchedule: FeedbackScheduleSchema(
      datetimeStart,
      datetimeEnd,
      eventTimezone,
      scheduledPublishAt
    ),
    notificationChannel: NotificationChannelSchema
  });

export type FeedbackSettingsFormInput = z.input<ReturnType<typeof getFeedbackScheduleSchema>>; // Form values
export type FeedbackSettingsFormValues = z.infer<ReturnType<typeof getFeedbackScheduleSchema>>; // DB values

export const FEEDBACK_SETTINGS_DEFAULTS: FeedbackSettingsFormValues = {
  isFeedbackAnonymous: true,
  feedbackSchedule: {
    quantity: 10,
    unit: SCHEDULE_UNIT.MINUTES,
    beforeEventEnds: true
  },
  notificationChannel: {
    email: true,
    slack: true
  }
};

export const convertDBToFormData = (
  dbData: FeedbackSettingsFormValues,
  eventTimezone?: string
): FeedbackSettingsFormInput => {
  const dbBeforeEventEnds = dbData.feedbackSchedule?.beforeEventEnds;

  let beforeEventEnds:
    | typeof FEEDBACK_SCHEDULE_TYPE[keyof typeof FEEDBACK_SCHEDULE_TYPE]
    | undefined;
  if (dbBeforeEventEnds === undefined) {
    beforeEventEnds = undefined;
  } else {
    beforeEventEnds = dbBeforeEventEnds
      ? FEEDBACK_SCHEDULE_TYPE.BEFORE_EVENT_ENDS
      : FEEDBACK_SCHEDULE_TYPE.AFTER_EVENT_ENDS;
  }

  return {
    isFeedbackAnonymous: dbData.isFeedbackAnonymous
      ? FEEDBACK_ANONYMITY.ANONYMOUS
      : FEEDBACK_ANONYMITY.NOT_ANONYMOUS,
    feedbackSchedule: {
      ...dbData.feedbackSchedule,
      beforeEventEnds,
      sendAt: dbData.feedbackSchedule?.sendAt
        ? convertSendAtToFormValue(dbData.feedbackSchedule.sendAt, eventTimezone)
        : undefined
    },
    notificationChannel: dbData.notificationChannel
  };
};
